import { useEffect, useState } from "react";
import { Input, Modal, Switch } from "antd";
import { CloseOutlined } from "@ant-design/icons";

import { Button } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import { getStateNameByAbbreviation } from "../../../utils/MapStateName";
import { moneyTypeLabel } from "../../../utils/MoneyTypeLabels";
import type {
  StateExperienceConfig,
  StateExperienceConfigUpdate,
} from "./stateConfigurations";

import "./stateConfiguration.css";

export type EditStateModalProps = {
  open: boolean;
  config: StateExperienceConfig | null;
  /** TCM client country for state-name lookup — defaults to USA. */
  country?: string;
  /**
   * When false, hide the Predictions section and preserve the existing
   * prediction payload on save (product off for this client).
   */
  showPrediction?: boolean;
  /**
   * When false, hide the rebet_coin toggle and leave any existing coin
   * entry in the currencies array untouched on save (non-sweepstakes client).
   */
  showSecondaryCurrency?: boolean;
  saving: boolean;
  onCancel: () => void;
  onSave: (update: StateExperienceConfigUpdate) => void;
};

const DEFAULT_MIN_SIGNUP_AGE = 18;
const NUMERIC_ONLY = /^\d*$/;
const MIN_SIGNUP_AGE = 1;
const MAX_SIGNUP_AGE = 99;

const PREDICTION_CASH = "prediction_cash";
const REBET_CASH = "rebet_cash";
const REBET_COIN = "rebet_coin";

/**
 * The age is held as the raw input string so a cleared field stays visibly
 * empty rather than snapping back to a coerced `0`, which means "empty" is a
 * reachable state that must not be saved.
 */
function isSignupAgeValid(rawAge: string): boolean {
  if (rawAge === "" || !NUMERIC_ONLY.test(rawAge)) return false;
  const age = Number(rawAge);
  return age >= MIN_SIGNUP_AGE && age <= MAX_SIGNUP_AGE;
}

function hasCurrency(currencies: string[] | undefined, key: string) {
  return (currencies ?? []).includes(key);
}

/** Keep unknown currencies (e.g. `p_cash`) while replacing known toggle keys. */
function mergeCurrencies(
  toggledKeys: Array<{ key: string; enabled: boolean }>,
  previous: string[],
): string[] {
  const known = new Set(toggledKeys.map(({ key }) => key));
  const extras = previous.filter((c) => !known.has(c));
  const enabled = toggledKeys.filter(({ enabled }) => enabled).map(({ key }) => key);
  return [...enabled, ...extras];
}

function buildFormState(config: StateExperienceConfig | null) {
  if (!config) {
    return {
      signupAge: String(DEFAULT_MIN_SIGNUP_AGE),
      predictionSports: false,
      predictionCulture: false,
      predictionRips: false,
      predictionCash: false,
      sweepstakesSportsbook: false,
      sweepstakesCasino: false,
      sweepstakesRips: false,
      rebetCash: false,
      rebetCoin: false,
    };
  }
  return {
    signupAge: String(config.experiences.prediction.minimum_signup_age),
    predictionSports: config.experiences.prediction.sports,
    predictionCulture: config.experiences.prediction.culture,
    predictionRips: config.experiences.prediction.rips,
    predictionCash: hasCurrency(
      config.experiences.prediction.currencies,
      PREDICTION_CASH,
    ),
    sweepstakesSportsbook: config.experiences.sweepstakes.sportsbook,
    sweepstakesCasino: config.experiences.sweepstakes.casino,
    sweepstakesRips: config.experiences.sweepstakes.rips,
    rebetCash: hasCurrency(config.experiences.sweepstakes.currencies, REBET_CASH),
    rebetCoin: hasCurrency(config.experiences.sweepstakes.currencies, REBET_COIN),
  };
}

type FormState = ReturnType<typeof buildFormState>;
type ToggleKey = Exclude<keyof FormState, "signupAge">;

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="state-config-modal-toggle-row">
      <Switch
        className="state-config-switch"
        checked={checked}
        onChange={onChange}
        aria-label={label}
      />
      <span className="state-config-modal-toggle-label">{label}</span>
    </div>
  );
}

function EditStateModal({
  open,
  config,
  country = "USA",
  showPrediction = true,
  showSecondaryCurrency = true,
  saving,
  onCancel,
  onSave,
}: EditStateModalProps) {
  const [form, setForm] = useState(() => buildFormState(config));

  useEffect(() => {
    if (open) {
      setForm(buildFormState(config));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, config]);

  // US-map fallback mirrors the table's stateLabel: the backend sends US
  // state codes even on non-US clients, so a country-map miss shouldn't
  // surface a raw code in the title.
  const stateName = config
    ? getStateNameByAbbreviation(config.state, country) ??
      getStateNameByAbbreviation(config.state, "USA") ??
      config.state
    : "";

  const signupAgeValid = !showPrediction || isSignupAgeValid(form.signupAge);

  const toggle = (key: ToggleKey) => (checked: boolean) =>
    setForm((s) => ({ ...s, [key]: checked }));

  const handleSave = () => {
    if (!signupAgeValid || !config) return;

    // Omit rebet_coin from the toggle set when secondary is off so
    // mergeCurrencies treats any existing coin entry as an untouched extra.
    const sweepstakesCurrencyToggles = [
      { key: REBET_CASH, enabled: form.rebetCash },
      ...(showSecondaryCurrency
        ? [{ key: REBET_COIN, enabled: form.rebetCoin }]
        : []),
    ];
    const sweepstakesCurrencies = mergeCurrencies(
      sweepstakesCurrencyToggles,
      config.experiences.sweepstakes.currencies,
    );

    // When Predictions is off, keep the stored prediction block untouched so
    // a sweepstakes-only edit can't wipe ages/currencies the admin never saw.
    const prediction = showPrediction
      ? {
          sports: form.predictionSports,
          culture: form.predictionCulture,
          minimum_signup_age: Number(form.signupAge),
          currencies: mergeCurrencies(
            [{ key: PREDICTION_CASH, enabled: form.predictionCash }],
            config.experiences.prediction.currencies,
          ),
          rips: form.predictionRips,
        }
      : config.experiences.prediction;

    onSave({
      experiences: {
        prediction,
        sweepstakes: {
          sportsbook: form.sweepstakesSportsbook,
          casino: form.sweepstakesCasino,
          currencies: sweepstakesCurrencies,
          rips: form.sweepstakesRips,
        },
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      closeIcon={<CloseOutlined />}
      footer={null}
      width={showPrediction ? 720 : 400}
      className="state-config-modal"
      destroyOnHidden
    >
      <div className="state-config-modal-body">
        <h2 className="state-config-modal-title">
          {formatString(AppStrings.PAM_STATE_CONFIGURATION_MODAL_TITLE, {
            state: stateName,
          })}
        </h2>

        <div
          className={
            showPrediction
              ? "state-config-modal-columns"
              : "state-config-modal-columns state-config-modal-columns--single"
          }
        >
        {showPrediction ? (
        <section
          className="state-config-modal-section"
          aria-labelledby="state-config-prediction-section"
          data-testid="state-config-prediction-section"
        >
          <h3
            id="state-config-prediction-section"
            className="state-config-modal-section-title"
          >
            {AppStrings.PAM_STATE_CONFIGURATION_COLUMN_GROUP_PREDICTION}
          </h3>

          <div className="state-config-modal-subsection">
            <span className="state-config-modal-subsection-title">
              {AppStrings.PAM_STATE_CONFIGURATION_SECTION_FEATURES}
            </span>
            <ToggleRow
              label={AppStrings.PAM_STATE_CONFIGURATION_COLUMN_SPORTS}
              checked={form.predictionSports}
              onChange={toggle("predictionSports")}
            />
            <ToggleRow
              label={AppStrings.PAM_STATE_CONFIGURATION_COLUMN_CULTURE}
              checked={form.predictionCulture}
              onChange={toggle("predictionCulture")}
            />
            <ToggleRow
              label={AppStrings.PAM_STATE_CONFIGURATION_COLUMN_RIPS}
              checked={form.predictionRips}
              onChange={toggle("predictionRips")}
            />
          </div>

          <div className="state-config-modal-field">
            <span className="state-config-modal-label">
              {AppStrings.PAM_STATE_CONFIGURATION_SIGNUP_AGE_LABEL}
            </span>
            <Input
              className="state-config-modal-number"
              inputMode="numeric"
              value={form.signupAge}
              status={signupAgeValid ? undefined : "error"}
              aria-invalid={!signupAgeValid}
              onChange={(e) => {
                const { value } = e.target;
                if (!NUMERIC_ONLY.test(value)) return;
                setForm((s) => ({ ...s, signupAge: value }));
              }}
              data-testid="state-config-signup-age-input"
            />
          </div>

          <div
            className="state-config-modal-subsection state-config-modal-subsection-currencies"
            data-testid="state-config-prediction-currencies"
          >
            <span className="state-config-modal-subsection-title">
              {AppStrings.PAM_STATE_CONFIGURATION_SECTION_CURRENCIES}
            </span>
            <ToggleRow
              label={moneyTypeLabel(PREDICTION_CASH)}
              checked={form.predictionCash}
              onChange={toggle("predictionCash")}
            />
          </div>
        </section>
        ) : null}

        <section
          className="state-config-modal-section"
          aria-labelledby="state-config-sweepstakes-section"
          data-testid="state-config-sweepstakes-section"
        >
          <h3
            id="state-config-sweepstakes-section"
            className="state-config-modal-section-title"
          >
            {AppStrings.PAM_STATE_CONFIGURATION_COLUMN_GROUP_SWEEPSTAKES}
          </h3>

          <div className="state-config-modal-subsection">
            <span className="state-config-modal-subsection-title">
              {AppStrings.PAM_STATE_CONFIGURATION_SECTION_FEATURES}
            </span>
            <ToggleRow
              label={AppStrings.PAM_STATE_CONFIGURATION_COLUMN_SPORTSBOOK}
              checked={form.sweepstakesSportsbook}
              onChange={toggle("sweepstakesSportsbook")}
            />
            <ToggleRow
              label={AppStrings.PAM_STATE_CONFIGURATION_COLUMN_CASINO}
              checked={form.sweepstakesCasino}
              onChange={toggle("sweepstakesCasino")}
            />
            <ToggleRow
              label={AppStrings.PAM_STATE_CONFIGURATION_COLUMN_RIPS}
              checked={form.sweepstakesRips}
              onChange={toggle("sweepstakesRips")}
            />
          </div>

          <div
            className="state-config-modal-subsection state-config-modal-subsection-currencies"
            data-testid="state-config-sweepstakes-currencies"
          >
            <span className="state-config-modal-subsection-title">
              {AppStrings.PAM_STATE_CONFIGURATION_SECTION_CURRENCIES}
            </span>
            <ToggleRow
              label={moneyTypeLabel(REBET_CASH)}
              checked={form.rebetCash}
              onChange={toggle("rebetCash")}
            />
            {showSecondaryCurrency ? (
              <ToggleRow
                label={moneyTypeLabel(REBET_COIN)}
                checked={form.rebetCoin}
                onChange={toggle("rebetCoin")}
              />
            ) : null}
          </div>
        </section>
        </div>

        <div className="state-config-modal-footer">
          <Button
            intent="neutral"
            variant="outline"
            color="var(--grey-600)"
            onClick={onCancel}
            disabled={saving}
            data-testid="state-config-modal-cancel"
          >
            {AppStrings.NO}
          </Button>
          <Button
            intent="primary"
            color="var(--orange-500)"
            onClick={handleSave}
            loading={saving}
            disabled={!signupAgeValid}
            data-testid="state-config-modal-save"
          >
            {saving
              ? AppStrings.SAVING
              : AppStrings.PAM_STATE_CONFIGURATION_SAVE_CONFIRM}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default EditStateModal;
